import React from 'react';
import { motion } from 'framer-motion';
import './Hero.css';

const Hero: React.FC = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="hero">
      <div className="container">
        <div className="hero-content">
          <motion.div 
            className="hero-text"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1 
              className="hero-title"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              <span className="neon-text animate-glow">DESARROLLADORES</span>
              <br />
              <span className="gradient-text">& ESPECIALISTAS EN</span>
              <br />
              <span className="neon-text animate-glow">PRIVACIDAD Y CIBERSEGURIDAD</span>
            </motion.h1>

            <motion.p 
              className="hero-description"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
            >
              ¡Hola! Somos <strong className="code-text">IMMOACTIVAT I ASSOCIATS SL</strong>, el partner tecnológico que 
              <strong> digitaliza tu negocio</strong> de extremo a extremo: desde el desarrollo del software hasta la automatización 
              con IA, el marketing digital y la ciberseguridad. Trabajamos para empresas, startups y profesionales que necesitan 
              soluciones reales que protejan sus datos y aceleren su crecimiento.
            </motion.p>

            <motion.div 
              className="hero-features"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
            >
              <div className="feature-item">
                <span className="feature-icon">📱</span>
                <span>Apps móviles nativas (Android &amp; iOS) y multiplataforma con Flutter / React Native</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">💻</span>
                <span>Aplicaciones web full-stack, marketplaces, dashboards y sitios corporativos</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🤖</span>
                <span>Automatización de tareas e integración de IA con agentes y modelos LLM</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">📈</span>
                <span>Marketing digital, campañas de Google &amp; Meta Ads y SEO técnico</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🛡️</span>
                <span>Auditorías de ciberseguridad, pentesting y reverse engineering</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🚀</span>
                <span>Digitalización end-to-end para empresas, comercios y startups</span>
              </div>
            </motion.div>

            <motion.div 
              className="hero-cta"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.8 }}
            >
              <button 
                className="btn-primary cta-primary"
                onClick={() => scrollToSection('projects')}
              >
                Ver Proyectos
              </button>
              <button 
                className="btn-secondary cta-secondary"
                onClick={() => scrollToSection('contact')}
              >
                Contáctanos
              </button>
            </motion.div>
          </motion.div>

          <motion.div 
            className="hero-visual"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
          >
            <div className="tech-stack">
              <motion.div 
                className="tech-item"
                animate={{ 
                  rotate: 360,
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                  scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                }}
              >
                <span className="tech-icon">⚛️</span>
                <span className="tech-name">React</span>
              </motion.div>
              
              <motion.div 
                className="tech-item"
                animate={{ 
                  rotate: -360,
                  scale: [1, 1.2, 1]
                }}
                transition={{ 
                  rotate: { duration: 25, repeat: Infinity, ease: "linear" },
                  scale: { duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }
                }}
              >
                <span className="tech-icon">📱</span>
                <span className="tech-name">Flutter</span>
              </motion.div>
              
              <motion.div 
                className="tech-item"
                animate={{ 
                  rotate: 360,
                  scale: [1, 1.15, 1]
                }}
                transition={{ 
                  rotate: { duration: 18, repeat: Infinity, ease: "linear" },
                  scale: { duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }
                }}
              >
                <span className="tech-icon">🐍</span>
                <span className="tech-name">Python</span>
              </motion.div>
              
              <motion.div 
                className="tech-item"
                animate={{ 
                  rotate: -360,
                  scale: [1, 1.3, 1]
                }}
                transition={{ 
                  rotate: { duration: 22, repeat: Infinity, ease: "linear" },
                  scale: { duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 1.5 }
                }}
              >
                <span className="tech-icon">🛡️</span>
                <span className="tech-name">CyberSec</span>
              </motion.div>
              
              <motion.div 
                className="tech-item center-tech"
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotateY: [0, 180, 360]
                }}
                transition={{ 
                  scale: { duration: 4, repeat: Infinity, ease: "easeInOut" },
                  rotateY: { duration: 6, repeat: Infinity, ease: "linear" }
                }}
              >
                <span className="tech-icon">🤖</span>
                <span className="tech-name">AI/ML</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
